import { useState } from "react";
import { Download, Share, MoreVertical, Plus, X, CheckCircle2, ExternalLink, BookmarkPlus } from "lucide-react";
import { usePwaInstall } from "@/lib/pwaInstall";

/**
 * "Install app" entry point that lives inside the site's own menus (mobile
 * hamburger and desktop nav). Hides entirely when the app is already running
 * in standalone mode — no point asking installed users to install again.
 *
 * On tap:
 *   - If Chrome already gave us a deferred `beforeinstallprompt`, we fire it
 *     directly (one-tap native install).
 *   - Otherwise we open a small instructions sheet whose content is tailored
 *     to the user's OS / browser (iOS Safari, Android Chrome menu, in-app
 *     browser fallback, desktop). No more "tap 3 dots" on an iPhone.
 */
export default function InstallAppMenuItem({ variant = "mobile", onAfterClick }) {
  const { standalone, canPrompt, ios, android, chromiumAndroid, inAppBrowser, install } = usePwaInstall();
  const [open, setOpen] = useState(false);
  const [justInstalled, setJustInstalled] = useState(false);

  // Already installed? Hide the entry point entirely.
  if (standalone) return null;

  const handleClick = async () => {
    if (canPrompt) {
      const { outcome } = await install();
      if (outcome === "accepted") {
        setJustInstalled(true);
        window.setTimeout(() => setJustInstalled(false), 4000);
      }
      onAfterClick?.();
      return;
    }
    // No deferred prompt → show platform-specific instructions.
    setOpen(true);
  };

  const triggerCls =
    variant === "desktop"
      ? "flex items-center gap-2 px-3 py-2 text-sm text-[#4A4A4A] hover:text-[#D62828] transition-colors"
      : "flex items-center gap-2 px-3 py-2 text-sm text-[#D62828] text-left";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={triggerCls}
        data-testid={`install-app-menu-${variant}`}
      >
        {justInstalled ? (
          <CheckCircle2 size={16} strokeWidth={1.75} className="text-[#3F8A4D]" />
        ) : (
          <Download size={16} strokeWidth={1.75} />
        )}
        <span>{justInstalled ? "App installed" : "Install app"}</span>
      </button>

      {open && (
        <InstallInstructionsModal
          ios={ios}
          android={android}
          chromiumAndroid={chromiumAndroid}
          inAppBrowser={inAppBrowser}
          onClose={() => { setOpen(false); onAfterClick?.(); }}
        />
      )}
    </>
  );
}

function InstallInstructionsModal({ ios, android, chromiumAndroid, inAppBrowser, onClose }) {
  // Pick a content variant. Order matters: in-app browser check first because
  // an iOS device inside Instagram still can't install via Safari's Share menu.
  let content;
  if (inAppBrowser) content = <InAppBrowserHelp ios={ios} android={android} />;
  else if (ios) content = <IosHelp />;
  else if (android) content = <AndroidHelp chromium={chromiumAndroid} />;
  else content = <DesktopHelp />;

  // Note: we intentionally use a click-to-dismiss backdrop AND a close
  // button — Indian Android users frequently miss small × glyphs on
  // bright-light screens.
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6 animate-[sv-fade-up_0.18s_ease-out]"
      onClick={onClose}
      data-testid="install-instructions-backdrop"
    >
      <div
        className="bg-white border border-[#F4A100] shadow-2xl max-w-md w-full max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="install-instructions-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E0D8] sticky top-0 bg-white">
          <div>
            <div className="font-serif text-lg text-[#D62828] leading-tight">Install Shubh Vivah</div>
            <div className="tracking-luxe text-[10px] text-[#F4A100]">by PAPER KRAFT</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-[#4A4A4A] hover:text-[#D62828]"
            aria-label="Close"
            data-testid="install-instructions-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5">{content}</div>
      </div>
    </div>
  );
}

/* ---------- per-platform bodies ---------- */

function StepRow({ n, icon, title, children }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#D62828] text-white text-xs font-semibold flex items-center justify-center mt-0.5">
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm text-[#1A1A1A]">
          <span>{title}</span>
          {icon}
        </div>
        {children && (
          <div className="text-xs text-[#4A4A4A] leading-relaxed mt-1">{children}</div>
        )}
      </div>
    </div>
  );
}

function IosHelp() {
  return (
    <div data-testid="install-help-ios">
      <p className="text-sm text-[#4A4A4A] leading-relaxed mb-3">
        iPhone &amp; iPad need a quick manual step — Apple doesn&apos;t allow a one-tap install.
      </p>
      <div className="divide-y divide-[#F0EBE0]">
        <StepRow
          n={1}
          title="Tap the Share button"
          icon={<span className="inline-flex items-center justify-center w-7 h-7 border border-[#F4A100] text-[#D62828]"><Share size={14} strokeWidth={1.75} /></span>}
        >
          It&apos;s the square with an upward arrow, usually at the <span className="font-medium">bottom centre</span> of Safari.
        </StepRow>
        <StepRow
          n={2}
          title="Scroll and tap"
          icon={<span className="font-medium text-[#D62828]">Add to Home Screen</span>}
        >
          You may need to swipe up inside the share sheet to find it.
        </StepRow>
        <StepRow
          n={3}
          title="Tap Add"
        >
          The Shubh Vivah icon will appear on your home screen, just like an app.
        </StepRow>
      </div>
      <div className="mt-4 bg-[#F8F5EE] border border-[#E8DFC8] px-3 py-2 text-[11px] text-[#4A4A4A] leading-relaxed">
        <strong className="text-[#D62828]">Tip:</strong> if you don&apos;t see a Share button, make sure you opened this page in <span className="font-medium">Safari</span> (not Chrome or another browser).
      </div>
    </div>
  );
}

function AndroidHelp({ chromium = false }) {
  return (
    <div data-testid="install-help-android">
      <p className="text-sm text-[#4A4A4A] leading-relaxed mb-3">
        {chromium
          ? "Your browser didn't show the one-tap install button this time. Use the browser menu:"
          : "Use your browser's menu to install:"}
      </p>
      <div className="divide-y divide-[#F0EBE0]">
        <StepRow
          n={1}
          title="Tap the menu"
          icon={<span className="inline-flex items-center justify-center w-7 h-7 border border-[#F4A100] text-[#D62828]"><MoreVertical size={14} strokeWidth={2} /></span>}
        >
          The <span className="font-medium">⋮ three-dots icon</span> at the <span className="font-medium">top-right</span> of Chrome / Edge / Brave (or top-left in some Samsung browsers).
        </StepRow>
        <StepRow
          n={2}
          title="Choose"
          icon={<span className="font-medium text-[#D62828]">Install app</span>}
        >
          On some browsers it&apos;s labelled <span className="font-medium">Add to Home screen</span>.
        </StepRow>
        <StepRow
          n={3}
          title="Confirm Install"
        >
          Shubh Vivah will open like a real app — fullscreen, no browser bars.
        </StepRow>
      </div>
      <div className="mt-4 bg-[#F8F5EE] border border-[#E8DFC8] px-3 py-2 text-[11px] text-[#4A4A4A] leading-relaxed">
        {chromium ? (
          <>
            <strong className="text-[#D62828]">Can&apos;t find Install in the ⋮ menu?</strong> Either Shubh Vivah is <span className="font-medium">already installed</span> on this phone (check your apps drawer for the Paper Kraft Cards icon), or your browser paused install prompts after a recent dismiss. Close all tabs of paperkraftcards.in and reopen the site in a new tab to try again.
          </>
        ) : (
          <>
            <strong className="text-[#D62828]">Don&apos;t see Install?</strong> Open this page in <span className="font-medium">Google Chrome</span>. Firefox, UC Browser and Opera Mini don&apos;t support installing websites.
          </>
        )}
      </div>
    </div>
  );
}

function DesktopHelp() {
  return (
    <div data-testid="install-help-desktop">
      <p className="text-sm text-[#4A4A4A] leading-relaxed mb-3">
        Install Shubh Vivah as a desktop app for quicker access:
      </p>
      <div className="divide-y divide-[#F0EBE0]">
        <StepRow
          n={1}
          title="Open the address bar"
        >
          Look at the right end of the URL bar for a small <Plus size={12} className="inline-block align-[-2px]" /> or monitor icon.
        </StepRow>
        <StepRow
          n={2}
          title="Click Install"
          icon={<span className="font-medium text-[#D62828]">Install Shubh Vivah</span>}
        >
          Or open the Chrome menu (⋮) → <span className="font-medium">Cast, save, share</span> → <span className="font-medium">Install page as app…</span>
        </StepRow>
        <StepRow
          n={3}
          title="Or bookmark this page"
          icon={<BookmarkPlus size={14} className="text-[#D62828]" />}
        >
          Press <kbd className="px-1.5 py-0.5 bg-[#F5F2EA] border border-[#E5E0D8] text-[10px]">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 bg-[#F5F2EA] border border-[#E5E0D8] text-[10px]">D</kbd> (or <kbd className="px-1.5 py-0.5 bg-[#F5F2EA] border border-[#E5E0D8] text-[10px]">⌘</kbd> + <kbd className="px-1.5 py-0.5 bg-[#F5F2EA] border border-[#E5E0D8] text-[10px]">D</kbd> on Mac) to save it.
        </StepRow>
      </div>
    </div>
  );
}

function InAppBrowserHelp({ ios }) {
  return (
    <div data-testid="install-help-in-app">
      <p className="text-sm text-[#4A4A4A] leading-relaxed mb-3">
        You&apos;re viewing Shubh Vivah inside another app (Instagram, Facebook, WhatsApp…) — these don&apos;t allow installing websites.
      </p>
      <div className="divide-y divide-[#F0EBE0]">
        <StepRow
          n={1}
          title="Open in your main browser"
          icon={<ExternalLink size={14} className="text-[#D62828]" />}
        >
          Tap the <span className="font-medium">⋮ menu</span> at the top of this in-app screen and choose <span className="font-medium">{ios ? "Open in Safari" : "Open in Chrome"}</span> (or copy the link and paste it into your browser).
        </StepRow>
        <StepRow
          n={2}
          title="Come back to this menu"
        >
          Open the site&apos;s menu again and tap <span className="font-medium text-[#D62828]">Install app</span>.
        </StepRow>
      </div>
    </div>
  );
}
